import React, { useState, useEffect, useCallback, useRef } from 'react';
import supabaseService from '../services/supabaseService';
import './AnnouncementBar.css';

// Fallback announcements if no database notices exist
const fallbackAnnouncements = [
  {
    id: 'fallback-1',
    icon: '🎉',
    title: 'Welcome to Government Degree College, Beekapur',
    content: 'Admissions are now open for the 2025-26 academic session.',
    cta: { label: 'Apply Now', href: '/register' },
    category: 'general',
    priority: 'normal',
  },
  {
    id: 'fallback-2',
    icon: '📚',
    title: 'Explore our academic programs',
    content: 'Discover courses, faculty, and resources available to students.',
    cta: { label: 'Explore Now', href: '/academics' },
    category: 'academic',
    priority: 'normal',
  },
  {
    id: 'fallback-3',
    icon: '💬',
    title: 'Need help getting started?',
    content: 'Our team is here to assist you with admissions and courses.',
    cta: { label: 'Learn More', href: '/courses' },
    category: 'general',
    priority: 'normal',
  },
];

// Category → icon mapping (matches BulletinBoard)
const getCategoryIcon = (category) => {
  switch (category) {
    case 'finance': return '💰';
    case 'events': return '🎉';
    case 'academic': return '📚';
    case 'exam': return '📝';
    case 'holiday': return '🏖️';
    case 'general': return '📢';
    default: return '📌';
  }
};

// Category → CTA mapping
const getCategoryCta = (category) => {
  switch (category) {
    case 'finance': return { label: 'View Details', href: '/students-corner' };
    case 'events': return { label: 'Learn More', href: '/gallery' };
    case 'academic': return { label: 'View Programs', href: '/academics' };
    case 'exam': return { label: 'Check Schedule', href: '/academics' };
    case 'holiday': return { label: 'View Calendar', href: '/academics' };
    case 'general': return { label: 'Read More', href: '/students-corner' };
    default: return { label: 'View Details', href: '/students-corner' };
  }
};

const AnnouncementBar = () => {
  const [announcements, setAnnouncements] = useState(fallbackAnnouncements);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const intervalRef = useRef(null);
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  // Fetch notices from Supabase
  useEffect(() => {
    const fetchNotices = async () => {
      try {
        const notices = await supabaseService.getActiveNotices();
        if (notices && notices.length > 0) {
          const mapped = notices.map((notice) => ({
            id: notice.id,
            icon: getCategoryIcon(notice.category),
            title: notice.title,
            content: notice.content,
            cta: getCategoryCta(notice.category),
            category: notice.category,
            priority: notice.priority,
          }));
          setAnnouncements(mapped);
        }
        // If no notices, keep fallback
      } catch (error) {
        console.error('Error fetching notices for announcement bar:', error);
        // Keep fallback on error
      }
    };

    fetchNotices();
  }, []);

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % announcements.length);
  }, [announcements.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + announcements.length) % announcements.length);
  }, [announcements.length]);

  // Auto-rotation
  useEffect(() => {
    if (isPaused || isDismissing || announcements.length <= 1) {
      clearInterval(intervalRef.current);
      return;
    }

    intervalRef.current = setInterval(() => {
      goToNext();
    }, 4500);

    return () => clearInterval(intervalRef.current);
  }, [isPaused, isDismissing, goToNext, announcements.length]);

  const handleDismiss = () => {
    setIsDismissing(true);
    setTimeout(() => setIsVisible(false), 400);
  };

  if (!isVisible) return null;

  return (
    <div
      className={`announcement-bar ${isDismissing ? 'dismissing' : ''}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      role="region"
      aria-label="Announcements"
    >
      <div className="announcement-bar-inner">
        {/* Left navigation arrow */}
        <button
          className="announcement-nav-btn announcement-nav-prev"
          onClick={goToPrev}
          aria-label="Previous announcement"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* Carousel content */}
        <div className="announcement-carousel-viewport">
          <div
            className={`announcement-carousel-track ${prefersReducedMotion.current ? 'no-transition' : ''}`}
            style={{
              transform: `translateX(-${currentIndex * 100}%)`,
              transition: prefersReducedMotion.current ? 'none' : 'transform 600ms cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {announcements.map((item) => (
              <div key={item.id} className="announcement-slide">
                <span className="announcement-icon">{item.icon}</span>
                <span className="announcement-text">
                  <strong>{item.title}</strong>
                  {item.content && <span className="announcement-content-text"> — {item.content}</span>}
                </span>
                <a href={item.cta.href} className="announcement-cta">
                  {item.cta.label}
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Right navigation arrow */}
        <button
          className="announcement-nav-btn announcement-nav-next"
          onClick={goToNext}
          aria-label="Next announcement"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        {/* Dot indicators */}
        {announcements.length > 1 && (
          <div className="announcement-dots">
            {announcements.map((_, index) => (
              <button
                key={index}
                className={`announcement-dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to announcement ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Close button */}
        <button
          className="announcement-close"
          onClick={handleDismiss}
          aria-label="Close announcement"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default AnnouncementBar;
